import {
    useCallback, useMemo,
} from 'react'

import useGetAccountLane from 'app/Apps/AccountManagement/AccountLanes/hooks/useGetAccountLane'
import StatusHandler from 'app/shared-components/StatusHandler'
import useHasPermission from 'app/hooks/useHasPermission'
import {
    LaneStatus,
} from 'app/enums/label.enums'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import {
    Location,
} from 'app/shared-components/LocationSelector'
import requests from 'app/requests/Lane/Lane.requests'
import Button from 'app/shared-components/Button'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import useCancelLane from 'app/Apps/AccountManagement/AQuotes/hooks/useCancelLane'
import useRejectLane from 'app/Apps/AccountManagement/AQuotes/hooks/useRejectLane'
import AwardRejectLaneForm from './AwardRejectLaneForm'
import useAwardLane from './hooks/useAwardLane'

const AwardRejectLane = ({
    id, refetch,
}: {
    id: number,
    refetch: (...args: any[]) => void
}) => {
    const ContactManagementRoutes = useContactManagementRoutes()
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetAccountLane(id)

    const onSuccess = useCallback(() => {
        refetch()
        invalidate()
    }, [
        refetch,
        invalidate,
    ])

    const award = useAwardLane({
        onSuccess,
    })

    const reject = useRejectLane({
        onSuccess,
    })

    const cancelLane = useCancelLane({
        id,
        onSuccess,
    })

    const hasCompanyReadPermission = useHasPermission(['company_read'])
    const hasEditAddressPermission = useHasPermission(['location_cs'])
    const showAddressesLinks = ![
        LaneStatus.AWARDED,
        LaneStatus.IMPLEMENTED,
    ].includes(data?.laneStatus)

    const getContactManagementLink = useCallback(() => {
        return showAddressesLinks && hasCompanyReadPermission ? [<SkyNetLink
            link={generateSkyNetLink({
                domainPath: ContactManagementRoutes.Companies.path,
            })}
            text="Contact Management"
            key="contact-management-link"
            type={TypeLink.EXTERNAL}
            size={SizeLink.S}
        />] : undefined
    }, [
        ContactManagementRoutes,
        hasCompanyReadPermission,
        showAddressesLinks,
    ])

    const getEditAddressLink = useCallback(({
        id: addressId,
    }: Location) => {
        return showAddressesLinks && hasEditAddressPermission ? [<SkyNetLink
            link={generateSkyNetLink({
                domainPath: ContactManagementRoutes.Locations.Update.path,
                params: {
                    key: addressId,
                },
            })}
            text="Edit Address"
            key="edit-address-link"
            type={TypeLink.EXTERNAL}
            size={SizeLink.S}
        />] : undefined
    }, [
        ContactManagementRoutes,
        hasEditAddressPermission,
        showAddressesLinks,
    ])

    const isAwardedRejected = useMemo(() => {
        return [
            LaneStatus.REJECTED_CUSTOMER,
            LaneStatus.AWARDED,
        ].includes(data?.laneStatus)
    }, [data?.laneStatus])

    const getCustomButtons = useCallback(({
        edited,
        isDisabledOnError,
    }) => {
        const result = [<Button
            onClick={cancelLane}
            key="cancel_lane"
            name="cancel_lane"
            label="Cancel Lane"
            disabled={edited}
        />]

        if (isAwardedRejected) {
            return result
        }

        return [
            <Button
                onClick={() => {
                    return award(id)
                }}
                key="award"
                name="award"
                label="Award Lane"
                disabled={edited || isDisabledOnError}
            />,
            <Button
                onClick={() => {
                    return reject(id)
                }}
                key="reject"
                name="reject"
                label="Reject Lane"
                disabled={edited}
            />,
            ...result,
        ]
    }, [
        cancelLane,
        isAwardedRejected,
        award,
        id,
        reject,
    ])

    return (
        <StatusHandler
            isError={isError}
            error={error}
            isFetching={isFetching}
            isSuccess={isDataReady}
        >
            <AwardRejectLaneForm
                data={data}
                disabled={[LaneStatus.IMPLEMENTED].includes(data?.laneStatus)}
                onSuccess={onSuccess}
                onCancel={invalidate}
                getCustomButtons={getCustomButtons}
                requestParam={requests.updateAwardRejectLaneData({
                    id,
                })}
                getContactManagementLink={getContactManagementLink}
                getEditHandoverAddressLink={
                    () => {
                        return getEditAddressLink(data?.handoverPoint)
                    }
                }
                getEditCollectionAddressLink={
                    () => {
                        return getEditAddressLink(data?.collectionDropoffPoint)
                    }
                }
                showMainActionButtons={!isAwardedRejected}
            />
        </StatusHandler>
    )
}

export default AwardRejectLane
